import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShoppingCart, BookOpen, Heart, MessageCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { api, apiError } from "@/lib/api";
import { bookUrl } from "@/lib/seo";
import { toast } from "sonner";

export default function BookCard({ book }) {
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { format, formatBook, formatBookDual, getOverrideBadges, isPriceOnRequest } = useCurrency();
  void format;
  const [wished, setWished] = useState(false);
  const priceOnRequest = isPriceOnRequest(book);
  const inquiryUrl = `https://wa.me/919953272630?text=${encodeURIComponent(`Hi GAINA THE PRELUDE, please share the price and availability for:\n\n${book.title}${book.author ? " by " + book.author : ""}${book.isbn ? " (ISBN " + book.isbn + ")" : ""}\n\nThank you.`)}`;

  useEffect(() => {
    let ignore = false;
    if (!user) { setWished(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/wishlist/ids");
        if (!ignore) setWished((data.ids || []).includes(book.id));
      } catch (e) { console.error(e); }
    })();
    return () => { ignore = true; };
  }, [user, book.id]);

  const toggleWish = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error("Please login to save books"); return; }
    try {
      const { data } = await api.post("/wishlist/toggle", { book_id: book.id });
      setWished(data.added);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div
      className="group relative border border-border bg-white flex flex-col hover-lift"
      data-testid={`book-card-${book.id}`}
    >
      <Link to={bookUrl(book)} className="block">
        <div className="aspect-[3/4] bg-[#f4f4f5] overflow-hidden border-b border-border relative">
          {book.image_url ? (
            <img
              src={book.image_url}
              alt={`${book.title}${book.author ? ` by ${book.author}` : ""}${book.isbn ? ` — ISBN ${book.isbn}` : ""} | GAINA THE PRELUDE`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
              onLoad={(e) => {
                const el = e.currentTarget;
                if (el.naturalWidth <= 2 || el.naturalHeight <= 2) {
                  el.style.display = "none";
                  const fb = el.parentElement && el.parentElement.querySelector('[data-testid="book-card-fallback"]');
                  if (fb) fb.style.display = "flex";
                }
              }}
              onError={(e) => {
                const el = e.currentTarget;
                el.style.display = "none";
                const fb = el.parentElement && el.parentElement.querySelector('[data-testid="book-card-fallback"]');
                if (fb) fb.style.display = "flex";
              }}
            />
          ) : null}
          <div
            className="w-full h-full items-center justify-center absolute inset-0"
            style={{ display: book.image_url ? "none" : "flex" }}
            data-testid="book-card-fallback"
          >
            <BookOpen className="w-12 h-12 text-[#a1a1aa]" />
          </div>
          {book.mrp && book.mrp > book.price && (
            <div className="absolute top-2 left-2 bg-[#ea580c] text-white text-[10px] font-bold px-2 py-0.5 tracking-wider">
              {Math.round(((book.mrp - book.price) / book.mrp) * 100)}% OFF
            </div>
          )}
          <button
            onClick={toggleWish}
            className={`absolute top-2 right-2 w-8 h-8 flex items-center justify-center border border-[#09090b] transition-colors ${wished ? "bg-[#09090b] text-white" : "bg-white/90 backdrop-blur hover:bg-[#09090b] hover:text-white"}`}
            data-testid={`wishlist-toggle-${book.id}`}
            aria-label="Toggle wishlist"
          >
            <Heart className={`w-4 h-4 ${wished ? "fill-current" : ""}`} />
          </button>
        </div>
      </Link>

      <div className="p-4 flex flex-col gap-2 flex-1">
        {book.publisher && (
          <div className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#ea580c] truncate">
            {book.publisher}
          </div>
        )}
        <Link to={bookUrl(book)} className="hover:underline">
          <h3 className="font-display font-bold text-sm leading-snug line-clamp-2 min-h-[2.5rem]" data-testid="book-title">
            {book.title}
          </h3>
        </Link>
        {book.author && (
          <div className="text-xs text-[#52525b] truncate">by {book.author}</div>
        )}
        <div className="flex flex-wrap gap-1">
          {book.class_level && (
            <span className="text-[10px] px-1.5 py-0.5 border border-border bg-[#f4f4f5] font-semibold">{book.class_level}</span>
          )}
          {book.subject && (
            <span className="text-[10px] px-1.5 py-0.5 border border-border bg-[#f4f4f5] font-semibold">{book.subject}</span>
          )}
        </div>
        <div className="mt-auto pt-3 flex flex-col gap-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span
              className={`font-display font-black ${priceOnRequest ? "text-sm md:text-base text-[#ea580c]" : "text-xl text-[#09090b]"}`}
              data-testid="book-price"
            >
              {formatBookDual(book, "price").primary}
            </span>
            {!priceOnRequest && formatBookDual(book, "price").secondary && (
              <span className="text-[11px] text-[#71717a] font-semibold" data-testid="book-price-secondary">≈ {formatBookDual(book, "price").secondary}</span>
            )}
            {!priceOnRequest && book.mrp && book.mrp > book.price && (
              <span className="text-xs line-through text-[#a1a1aa]">{formatBook(book, "mrp")}</span>
            )}
          </div>
          {getOverrideBadges(book, "price").length > 0 && (
            <div className="flex flex-wrap gap-1" data-testid="book-price-badges">
              {getOverrideBadges(book, "price").map((b) => (
                <span key={b.currency} className="text-[10px] px-1.5 py-0.5 border border-[#ea580c] text-[#ea580c] font-bold">
                  {b.symbol}{b.value}
                </span>
              ))}
            </div>
          )}
        </div>
        {priceOnRequest ? (
          <a
            href={inquiryUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white text-sm font-bold py-2.5 transition-colors flex items-center justify-center gap-2"
            data-testid={`inquire-now-${book.id}`}
          >
            <MessageCircle className="w-4 h-4" /> Inquire Now
          </a>
        ) : (
          <button
            onClick={() => addToCart(book.id, 1)}
            className="w-full bg-[#09090b] text-white text-sm font-bold py-2.5 hover:bg-[#ea580c] transition-colors flex items-center justify-center gap-2"
            data-testid={`add-to-cart-${book.id}`}
          >
            <ShoppingCart className="w-4 h-4" /> Add to Cart
          </button>
        )}
      </div>
    </div>
  );
}
